/**
 * Maps home-background `backgroundColor` tokens (the same tokens stored in
 * `profile_data.layout.backgroundColor`, and defined by the design system's
 * `AvatarColorVariant` enum, e.g. `color-primary`, `gradient-red-purple`) to
 * pre-rendered background image assets, for both solid colors and gradients.
 *
 * Every image here has NO overlay baked into its pixels. The dark overlay is
 * applied live via CSS using the single `--home-background-overlay-opacity`
 * variable (defined in `src/app.css`), for both:
 *  - the actual rendered background (see `src/routes/MainLayout.svelte` /
 *    `MainLayoutNew.svelte`, which override the design system's own
 *    `--background-image-overlay` variable scoped to just the background layer)
 *  - the background picker swatch preview (see `src/components/Lists/ImageListV2.svelte`,
 *    which draws a matching overlay on top of the swatch thumbnail)
 *
 * Keeping the overlay CSS-driven (instead of pre-composited into the image files)
 * means the opacity only needs to be changed in one place to retune it everywhere.
 */

const COLORS_PATH = 'images/home_def/colors';
const GRADIENTS_PATH = 'images/home_def/gradients';

export type BackgroundSolidColorToken =
	| 'color-0'
	| 'color-primary-10'
	| 'color-primary'
	| 'color-green'
	| 'color-cyan'
	| 'color-lightblue'
	| 'color-darkblue'
	| 'color-purple'
	| 'color-pink'
	| 'color-red'
	| 'color-peach'
	| 'color-orange'
	| 'color-yellow'
	| 'color-base';

export type BackgroundGradientToken =
	| 'gradient-blue-light'
	| 'gradient-blue-purple'
	| 'gradient-cyan-light'
	| 'gradient-pink-purple'
	| 'gradient-green-yellow'
	| 'gradient-cyan-blue'
	| 'gradient-orange-light'
	| 'gradient-yellow-gold'
	| 'gradient-red-purple'
	| 'gradient-royal-purple'
	| 'gradient-orange-magenta'
	| 'gradient-blue-green';

export type BackgroundColorToken = BackgroundSolidColorToken | BackgroundGradientToken;

export interface BackgroundColorImage {
	full: string;
	thumb: string;
}

const solidColorImage = (token: BackgroundSolidColorToken): BackgroundColorImage => ({
	full: `${COLORS_PATH}/${token}.png`,
	thumb: `${COLORS_PATH}/${token}_thumb.png`,
});

const gradientImage = (token: BackgroundGradientToken): BackgroundColorImage => ({
	full: `${GRADIENTS_PATH}/${token}.png`,
	thumb: `${GRADIENTS_PATH}/${token}_thumb.png`,
});

export const BACKGROUND_COLOR_IMAGES: Record<BackgroundColorToken, BackgroundColorImage> = {
	'color-0': solidColorImage('color-0'),
	'color-primary-10': solidColorImage('color-primary-10'),
	'color-primary': solidColorImage('color-primary'),
	'color-green': solidColorImage('color-green'),
	'color-cyan': solidColorImage('color-cyan'),
	'color-lightblue': solidColorImage('color-lightblue'),
	'color-darkblue': solidColorImage('color-darkblue'),
	'color-purple': solidColorImage('color-purple'),
	'color-pink': solidColorImage('color-pink'),
	'color-red': solidColorImage('color-red'),
	'color-peach': solidColorImage('color-peach'),
	'color-orange': solidColorImage('color-orange'),
	'color-yellow': solidColorImage('color-yellow'),
	'color-base': solidColorImage('color-base'),
	'gradient-blue-light': gradientImage('gradient-blue-light'),
	'gradient-blue-purple': gradientImage('gradient-blue-purple'),
	'gradient-cyan-light': gradientImage('gradient-cyan-light'),
	'gradient-pink-purple': gradientImage('gradient-pink-purple'),
	'gradient-green-yellow': gradientImage('gradient-green-yellow'),
	'gradient-cyan-blue': gradientImage('gradient-cyan-blue'),
	'gradient-orange-light': gradientImage('gradient-orange-light'),
	'gradient-yellow-gold': gradientImage('gradient-yellow-gold'),
	'gradient-red-purple': gradientImage('gradient-red-purple'),
	'gradient-royal-purple': gradientImage('gradient-royal-purple'),
	'gradient-orange-magenta': gradientImage('gradient-orange-magenta'),
	'gradient-blue-green': gradientImage('gradient-blue-green'),
};

/**
 * Resolves a `backgroundColor` token (as stored in `profile_data.layout.backgroundColor`)
 * to its pre-rendered image paths, if one exists.
 */
export function getBackgroundColorImage(token?: string): BackgroundColorImage | undefined {
	if (!token) return undefined;
	return BACKGROUND_COLOR_IMAGES[token as BackgroundColorToken];
}

/** Builds the `backgroundColorPreviewImages` map (token -> thumb path) for the background picker. */
export function getBackgroundColorPreviewImages(): Record<string, string> {
	return Object.fromEntries(
		Object.entries(BACKGROUND_COLOR_IMAGES).map(([token, { thumb }]) => [token, thumb]),
	);
}
